import React, { useEffect, useMemo, useRef, useState } from "react";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import { Link, useParams } from "react-router-dom";
import Error from "../Components/Error";
import TransactionsTableEfficient from "../Components/TransactionsTableEfficient";
import ActivityBarChart, { ActivityBucket } from "../Components/ActivityBarChart";
import Spinner from "../Components/Spinner";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import {
  HiQrCode, HiUsers, HiSparkles, HiGift, HiCheckCircle,
  HiTrophy, HiBellAlert, HiUserPlus, HiBolt, HiChartBar,
} from "react-icons/hi2";
import { FaUserCheck, FaUserSlash } from "react-icons/fa6";

// Uses the lightweight /Dashboard endpoints (SQL aggregates, no full template graph).

interface Summary {
  id: string;
  name: string;
  pointImage?: string;
  pointsToCollect: number;
}

interface TopCustomer {
  id: string;
  name: string;
  phone: string;
  points: number;
  radeemedRewards: number;
}

interface PeriodTopCustomer {
  id: string;
  name: string;
  phone: string;
  pointsEarned: number;
}

interface Snapshot {
  customers: number;
  points: number;
  rewards: number;
  rewardsRadeemed: number;
  activeCustomers: number;
  inactiveCustomers: number;
  activationRate: number;
  avgPoints: number;
  nearRewardCount: number;
  topCustomers: TopCustomer[];
}

interface Period {
  newCustomers: number;
  activeCustomers: number;
  pointsIssued: number;
  pointsDeducted: number;
  rewardsRedeemed: number;
  notificationsSent: number;
  topCustomers: PeriodTopCustomer[];
  series: { granularity: "day" | "month"; buckets: ActivityBucket[] };
}

interface Overview {
  pointsToCollect: number;
  snapshot: Snapshot;
  period: Period | null;
}

type Preset = "7d" | "30d" | "90d" | "12m" | "custom";

const PRESET_DAYS: Record<Exclude<Preset, "custom">, number> = { "7d": 7, "30d": 30, "90d": 90, "12m": 365 };
const MAX_RANGE_DAYS = 366;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const toInputValue = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromInputValue = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// [from, to) in the viewer's calendar days, sent to the API as UTC instants.
const presetRange = (preset: Exclude<Preset, "custom">) => {
  const tomorrow = addDays(startOfDay(new Date()), 1);
  return { from: addDays(tomorrow, -PRESET_DAYS[preset]), to: tomorrow };
};

const StatCard = ({
  icon, label, value, sub, color,
}: {
  icon: React.ReactNode; label: string; value: string | number; sub?: string; color: string;
}) => (
  <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-4 flex items-center gap-3">
    <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xl font-black text-gray-900 leading-none">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-brand-500 mt-0.5 font-medium">{sub}</p>}
    </div>
  </div>
);

const InsightCard = ({
  icon, label, value, sub, color, right,
}: {
  icon: React.ReactNode; label: string; value: string | number;
  sub?: string; color: string; right?: React.ReactNode;
}) => (
  <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-4 flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      {right}
    </div>
    <div>
      <p className="text-2xl font-black text-gray-900 leading-none">{value}</p>
      <p className="text-xs text-gray-500 mt-1 leading-snug">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5 leading-snug">{sub}</p>}
    </div>
  </div>
);

const Medal = ({ index }: { index: number }) => {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <span className="text-lg w-6 text-center flex-shrink-0">
      {medals[index] ?? <span className="text-xs font-bold text-gray-400">#{index + 1}</span>}
    </span>
  );
};

const PassTemplateDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const [summary, setSummary] = useState<Summary | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [imgSrc, setImgSrc] = useState("");

  const [preset, setPreset] = useState<Preset>("30d");
  const [range, setRange] = useState(() => presetRange("30d"));
  const [customFrom, setCustomFrom] = useState(() => toInputValue(range.from));
  const [customTo, setCustomTo] = useState(() => toInputValue(addDays(range.to, -1)));
  const [rangeError, setRangeError] = useState("");

  const rangeParams = useMemo(() => ({
    from: range.from.toISOString(),
    to: range.to.toISOString(),
  }), [range]);

  // Template summary: only what this page renders.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError("");
    OrganizationAxiosInstance.get<Summary>(`/Dashboard/${id}/Summary`)
      .then(r => { if (!cancelled) setSummary(r.data); })
      .catch(() => { if (!cancelled) setError("something went wrong getting program details"); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Snapshot + period stats; runs in parallel with the summary and re-runs when the range changes.
  const requestSeq = useRef(0);
  useEffect(() => {
    const seq = ++requestSeq.current;
    setOverviewLoading(true);
    setOverviewError(false);
    OrganizationAxiosInstance.get<Overview>(`/Dashboard/${id}/Overview`, {
      params: { ...rangeParams, tzOffsetMinutes: -new Date().getTimezoneOffset() },
    })
      .then(r => { if (seq === requestSeq.current) setOverview(r.data); })
      .catch(e => {
        if (seq !== requestSeq.current) return;
        console.error("Overview error:", e?.response?.data ?? e?.message);
        setOverviewError(true);
      })
      .finally(() => { if (seq === requestSeq.current) setOverviewLoading(false); });
  }, [id, rangeParams]);

  useEffect(() => {
    if (!summary?.id) return;
    const url = `${window.location.origin}/CustomerSignup/${summary.id}`;
    QRCode.toDataURL(url, { width: 200 }, (err, dataUrl) => {
      if (!err) setImgSrc(dataUrl);
    });
  }, [summary]);

  const choosePreset = (p: Preset) => {
    setPreset(p);
    setRangeError("");
    if (p !== "custom") setRange(presetRange(p));
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) {
      setRangeError(t("invalidRange"));
      return;
    }
    const from = fromInputValue(customFrom);
    const to = addDays(fromInputValue(customTo), 1); // end date is inclusive for the user
    const days = Math.round((to.getTime() - from.getTime()) / 86400000);
    if (days < 1 || days > MAX_RANGE_DAYS) {
      setRangeError(t("invalidRange"));
      return;
    }
    setRangeError("");
    setRange({ from, to });
  };

  const pointIconUrl = useMemo(() => {
    if (!summary?.pointImage) return null;
    try {
      const p = new URL(summary.pointImage).pathname.split("/");
      return `${process.env.REACT_APP_HOST}Images/${p[p.length - 2]}/${p[p.length - 1]}`;
    } catch {
      return null;
    }
  }, [summary]);

  if (isLoading) return <Spinner size={70} loading />;
  if (error) return <Error message={error} />;

  const snapshot = overview?.snapshot;
  const period = overview?.period;
  const ptc = overview?.pointsToCollect ?? summary?.pointsToCollect ?? 0;
  const signupUrl = `${window.location.origin}/CustomerSignup/${summary?.id}`;
  const redemptionRate = snapshot && snapshot.rewardsRadeemed + snapshot.rewards > 0
    ? Math.round((snapshot.rewardsRadeemed / (snapshot.rewardsRadeemed + snapshot.rewards)) * 100)
    : 0;
  const fmt = (n?: number) => (n === undefined ? "—" : n.toLocaleString());
  const hasActivity = !!period && period.series.buckets.some(b => b.points > 0 || b.rewards > 0);

  const presets: { key: Preset; label: string }[] = [
    { key: "7d", label: t("last7Days") },
    { key: "30d", label: t("last30Days") },
    { key: "90d", label: t("last90Days") },
    { key: "12m", label: t("last12Months") },
    { key: "custom", label: t("customRange") },
  ];

  return (
    <div className="space-y-6">

      {/* ══ Current status (all time) ══ */}
      <div>
        <div className="flex items-baseline gap-2 mb-3">
          <h2 className="text-sm font-bold text-gray-900">{t("currentStatus")}</h2>
          <span className="text-xs text-gray-400">{t("currentStatusSub")}</span>
        </div>

        {overviewError && !overview && (
          <p className="mb-3 text-xs text-red-500">{t("failedToLoad")}</p>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<HiUsers className="w-5 h-5 text-brand-600" />}
            color="bg-brand-50"
            label={t("customers")}
            value={fmt(snapshot?.customers)}
          />
          <StatCard
            icon={<HiSparkles className="w-5 h-5 text-violet-600" />}
            color="bg-violet-50"
            label={t("totalPoints")}
            value={fmt(snapshot?.points)}
          />
          <StatCard
            icon={<HiGift className="w-5 h-5 text-emerald-600" />}
            color="bg-emerald-50"
            label={t("rewardsRedeemed")}
            value={fmt(snapshot?.rewardsRadeemed)}
          />
          <StatCard
            icon={<HiCheckCircle className="w-5 h-5 text-amber-600" />}
            color="bg-amber-50"
            label={t("redemptionRate")}
            value={`${redemptionRate}%`}
            sub={`${snapshot?.rewards.toLocaleString() ?? 0} ${t("rewardsAvailable")}`}
          />
        </div>
      </div>

      {/* ── Insights Row ── */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-3">{t("insights")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <InsightCard
            icon={<FaUserCheck className="w-4 h-4 text-emerald-600" />}
            color="bg-emerald-50"
            label={t("activationRate")}
            value={`${snapshot?.activationRate ?? 0}%`}
            sub={`${snapshot?.activeCustomers ?? 0} ${t("activeCustomers")}`}
            right={
              <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
                <circle cx="18" cy="18" r="14" fill="none" stroke="#f0fdf4" strokeWidth="4" />
                <circle
                  cx="18" cy="18" r="14" fill="none"
                  stroke="#10b981" strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={`${(snapshot?.activationRate ?? 0) * 0.88} 88`}
                />
              </svg>
            }
          />
          <InsightCard
            icon={<FaUserSlash className="w-4 h-4 text-red-400" />}
            color="bg-red-50"
            label={t("inactiveCustomers")}
            value={snapshot?.inactiveCustomers ?? "—"}
            sub={snapshot && snapshot.customers > 0
              ? `${Math.round((snapshot.inactiveCustomers / snapshot.customers) * 100)}%`
              : undefined}
          />
          <InsightCard
            icon={<HiSparkles className="w-4 h-4 text-violet-600" />}
            color="bg-violet-50"
            label={t("avgPoints")}
            value={snapshot?.avgPoints ?? "—"}
          />
          <InsightCard
            icon={<HiBellAlert className="w-4 h-4 text-amber-600" />}
            color="bg-amber-50"
            label={t("nearReward")}
            value={snapshot?.nearRewardCount ?? "—"}
            sub="≥ 75%"
          />
        </div>
      </div>

      {/* ── Top Customers (all time) ── */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <HiTrophy className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-bold text-gray-900">{t("topCustomers")}</h3>
        </div>

        {snapshot?.topCustomers.length ? (
          <div className="divide-y divide-gray-100">
            {snapshot.topCustomers.map((c, i) => {
              const currentCyclePoints = ptc > 0 ? c.points % ptc : 0;
              return (
                <div key={c.id} className="flex items-center gap-4 px-6 py-3 hover:bg-brand-50/40 transition">
                  <Medal index={i} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.phone}</p>
                    {/* Point icons showing progress in current cycle */}
                    {pointIconUrl && ptc > 0 && (
                      <div className="flex flex-wrap gap-0.5 mt-1.5">
                        {Array.from({ length: Math.min(ptc, 20) }).map((_, idx) => (
                          <img
                            key={idx}
                            src={pointIconUrl}
                            alt=""
                            className="w-4 h-4 object-contain"
                            style={{ opacity: idx < currentCyclePoints ? 1 : 0.2 }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-end flex-shrink-0">
                    <p className="text-sm font-black text-gray-900">{c.points.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{t("points")}</p>
                    {c.radeemedRewards > 0 && (
                      <p className="text-xs text-emerald-600 font-medium">{c.radeemedRewards}× {t("redeemed")}</p>
                    )}
                  </div>
                  <Link
                    to={`/Dashboard/Program/${id}/Customer/${c.id}`}
                    className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-brand-50 text-brand-600 font-semibold hover:bg-brand-100 transition"
                  >
                    {t("details")}
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="px-6 py-8 text-sm text-gray-400 text-center">{t("noTopCustomers")}</p>
        )}
      </div>

      {/* ══ Activity (date filtered) ══ */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-sm font-bold text-gray-900">{t("activityInPeriod")}</h2>
          <div className="inline-flex flex-wrap rounded-xl border border-brand-100 bg-white p-1 shadow-sm self-start">
            {presets.map(p => (
              <button
                key={p.key}
                onClick={() => choosePreset(p.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                  preset === p.key ? "bg-brand-600 text-white" : "text-gray-600 hover:bg-brand-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {preset === "custom" && (
          <div className="flex flex-wrap items-end gap-3 bg-white rounded-2xl border border-brand-100 shadow-sm p-4">
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              {t("rangeFrom")}
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={e => setCustomFrom(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-900 focus:border-brand-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              {t("rangeTo")}
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                max={toInputValue(new Date())}
                onChange={e => setCustomTo(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-900 focus:border-brand-400 focus:outline-none"
              />
            </label>
            <button
              onClick={applyCustomRange}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition"
            >
              {t("apply")}
            </button>
            {rangeError && <p className="w-full text-xs text-red-500">{rangeError}</p>}
          </div>
        )}

        <div className={`space-y-4 transition-opacity ${overviewLoading && overview ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<HiUserPlus className="w-5 h-5 text-brand-600" />}
              color="bg-brand-50"
              label={t("newCustomers")}
              value={fmt(period?.newCustomers)}
            />
            <StatCard
              icon={<HiBolt className="w-5 h-5 text-emerald-600" />}
              color="bg-emerald-50"
              label={t("activeCustomers")}
              value={fmt(period?.activeCustomers)}
              sub={t("activeInPeriodSub")}
            />
            <StatCard
              icon={<HiSparkles className="w-5 h-5 text-violet-600" />}
              color="bg-violet-50"
              label={t("pointsIssued")}
              value={fmt(period?.pointsIssued)}
              sub={period && period.pointsDeducted > 0 ? `${period.pointsDeducted.toLocaleString()} ${t("pointsDeductedSub")}` : undefined}
            />
            <StatCard
              icon={<HiGift className="w-5 h-5 text-amber-600" />}
              color="bg-amber-50"
              label={t("rewardsRedeemed")}
              value={fmt(period?.rewardsRedeemed)}
              sub={period && period.notificationsSent > 0 ? `${period.notificationsSent.toLocaleString()} ${t("notificationsSentSub")}` : undefined}
            />
          </div>

          {/* Points issued over time */}
          <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <HiChartBar className="w-4 h-4 text-brand-600" />
              <h3 className="text-sm font-bold text-gray-900">
                {period?.series.granularity === "month" ? t("pointsIssuedPerMonth") : t("pointsIssuedPerDay")}
              </h3>
            </div>
            {period && hasActivity ? (
              <ActivityBarChart buckets={period.series.buckets} granularity={period.series.granularity} />
            ) : (
              <p className="py-10 text-sm text-gray-400 text-center">
                {overviewLoading ? "…" : overviewError ? t("failedToLoad") : t("noActivity")}
              </p>
            )}
          </div>

          {/* Top customers in the selected period */}
          <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <HiTrophy className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-bold text-gray-900">{t("topCustomersInPeriod")}</h3>
            </div>
            {period?.topCustomers.length ? (
              <div className="divide-y divide-gray-100">
                {period.topCustomers.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-4 px-6 py-3 hover:bg-brand-50/40 transition">
                    <Medal index={i} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                      <p className="text-xs text-gray-400">{c.phone}</p>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <p className="text-sm font-black text-gray-900">+{c.pointsEarned.toLocaleString()}</p>
                      <p className="text-xs text-gray-400">{t("earned")}</p>
                    </div>
                    <Link
                      to={`/Dashboard/Program/${id}/Customer/${c.id}`}
                      className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-brand-50 text-brand-600 font-semibold hover:bg-brand-100 transition"
                    >
                      {t("details")}
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-6 py-8 text-sm text-gray-400 text-center">{t("noActivity")}</p>
            )}
          </div>
        </div>

        {/* ── Transactions in the selected period ── */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-900">{t("recentTransactions")}</h3>
          </div>
          <div className="overflow-x-auto">
            {id && <TransactionsTableEfficient passTemplateId={id} from={rangeParams.from} to={rangeParams.to} />}
          </div>
        </div>
      </div>

      {/* ── QR Code ── */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <HiQrCode className="w-5 h-5 text-brand-600" />
          <h3 className="text-sm font-bold text-gray-900">{t("customerRegistrationQrCode")}</h3>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {imgSrc && (
            <img src={imgSrc} alt="QR code" className="w-36 h-36 rounded-xl border border-brand-100 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 mb-2">{t("customerRegistrationQrCode")}</p>
            <Link
              to={`/CustomerSignup/${summary?.id}`}
              className="block text-sm text-brand-600 hover:text-brand-700 break-all font-medium"
            >
              {signupUrl}
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PassTemplateDashboard;
